import {
  Box,
  Chip,
  Typography,
  AccordionSummary,
  AccordionDetails,
  Accordion,
  LinearProgress,
  Badge,
  useTheme
} from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import { closingConditionTooltipMessageHTML, postClosingConditionTooltipMessageHTML, LoanProcess, LoanStatus, uniq, closingConditionsLevel, isNotBlank, isNil, shouldHideTaskUploadTab } from '@roc/feature-utils';
import {
  Button,
  CopyText,
  IconButton,
  PrintableArea,
  PrintableAreaRef,
  StandardDialog,
  StatusChip,
  StatusType,
  Tooltip,
  TooltipOnOverflow,
} from '@roc/ui';
import { sanitizeHtml } from '@roc/ui/utils';
import { observer } from 'mobx-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CustomCard } from '../customCard';
import { TodoItem, TodoStatus } from '../../utils/loanDetailsTypes';
import { DownloadTodoButton, TodoActions, TodoTitleLink } from './todoActions';
import { Add, ExpandMore, InsertDriveFile, PhotoCamera, Remove, FileCopyOutlined, ArrowUpwardRounded } from '@material-ui/icons';
import { useDocumentStore } from '@roc/feature-documents';
import { StatusFilterField } from '../common/statusFilterField';
import { SectionActions } from './sectionActions';
import { getTodosWithSubtodos, filterTodosByStatus } from '../../utils/todoUtils';
import { useBaseStore, useUserStore } from '@roc/feature-app-core';
import { Alert, AlertTitle } from '@material-ui/lab';
import { colors } from '../../utils/todoConstants';
import { ReactComponent as ChatBubble } from '../../../icons/chatBubble.svg';
import { ReactComponent as ChatBubbleRead } from '../../../icons/chatBubbleRead.svg';
import { ReactComponent as ClosingConditionIcon } from '../../../icons/closingConditionIcon.svg';
import { ReactComponent as ClosingConditionWarning } from '../../../icons/closingConditionWarningIcon.svg';
import { ReactComponent as PostClosingConditionIcon } from '../../../icons/postClosingConditionIcon.svg';
import classname from 'classnames';
import { useLoanStore } from '@roc/feature-loans';
import { LineItemChat } from '../lineItemChat';
import { TodoStatusChip } from '../todoStatusChip';
import { mapApiShowOnlyPending, mapPriorityFilter, mapStatusFilter, SHOW_ALL_TO_DOS, StatusFilterType, todoStatusMapping, ESCALATED_TASK_TYPE } from '../../utils/loanDetailsConstants';
import { Document } from '@roc/feature-types';
import { CustomTaskDialog, useGeneralTasksStore } from '@roc/feature-general-tasks';
import { TaskScopeIcon } from '../common/taskScopeIcon';

interface StylesProps {
  depth?: number;
  isSummaryView?: boolean;
}

const TODO_COLUMN_WIDTHS = {
  assignee: '20%',
  priority: '15%',
  status: '25%',
  date: '30%',
  actions: '10%',
};

const TODO_COLUMN_WIDTHS_SUMMARY = {
  assignee: '28%',
  priority: '22%',
  status: '35%',
  actions: '15%',
};

const getColumnWidths = (isSummaryView: boolean) =>
  isSummaryView ? TODO_COLUMN_WIDTHS_SUMMARY : TODO_COLUMN_WIDTHS;

const getTodoColumnsMinWidth = (isInternalPortal: boolean, isSummaryView = false) => {
  if (isSummaryView) {
    return isInternalPortal ? '520px' : '480px';
  }
  return isInternalPortal ? '700px' : '630px';
};

const useStyles = makeStyles((theme: Theme) => ({
  closingConditionTooltip: {
    '& .tooltip': {
      maxWidth: '257px',
    },
  },
  category: {
    backgroundColor: colors.greenWhite,
    padding: '0px 10px',
    borderRadius: '6px 6px 6px 6px',
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: '10px',
    flex: '1 0 0',
    minHeight: '35px !important',
    cursor: 'pointer',
    userSelect: 'text',
    '& .MuiAccordionSummary-content': {
      margin: '0',
    },
    '& .MuiAccordionSummary-root': {
      minHeight: '35px !important',
    },
    '&.Mui-expanded': {
      borderRadius: '6px 6px 0px 0px',
    },
  },
  borderLinearProgress: {
    height: '18px',
    borderRadius: 20,
    [`& .MuiLinearProgress-root`]: {
      backgroundColor: colors.lavenderPinocchio,
    },
    [`& .MuiLinearProgress-bar`]: {
      borderRadius: 20,
      backgroundColor: colors.dodgerBlue,
    },
  },
  accordion: {
    marginBottom: '20px',
    '&.Mui-expanded': {
      margin: 0,
      marginBottom: '20px',
    },
    '&::before': {
      display: 'none',
    },
  },
  categoryTypography: {
    lineHeight: 2.5,
    fontWeight: 700,
    paddingRight: '8px'
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    padding: props => `8px 0px 8px ${props.depth * 34 + 44 + (props.depth > 0 ? 30 : 0)}px`,
    paddingRight: '0px',
    borderBottom: `1px solid ${colors.mercury}`,
    gap: '10px',
    backgroundColor: colors.soapstone,
    position: 'relative',
  },
  closingConditionIcon: {
    display: 'flex',
    position: 'absolute',
    left: '15px'
  },
  itemHasIcon: {
    padding: '16px 34px',
    paddingRight: '0px',
  },
  subItem: {
    paddingLeft: (props: StylesProps) => `${props.depth * 20}px`,
  },
  icon: {
    color: '#555',
    marginLeft: '-6px',
    cursor: 'pointer',
  },
  chip: {
    borderColor: 'black',
  },
  overflowElipsis: {
    maxWidth: (props: StylesProps) => (props.isSummaryView ? '100%' : '300px'),
    width: (props: StylesProps) => (props.isSummaryView ? '100%' : undefined),
    textWrap: 'nowrap',
    fontSize: (props: StylesProps) => (props.isSummaryView ? '15px' : '1rem'),
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  underlined: {
    textDecoration: 'underline',
    cursor: 'pointer',
  },
  bold: {
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  todoTitle: {
    display: (props: StylesProps) => (props.isSummaryView ? 'block' : 'inline-block'),
    flex: (props: StylesProps) => (props.isSummaryView ? 1 : undefined),
    minWidth: (props: StylesProps) => (props.isSummaryView ? 0 : undefined),
    overflow: (props: StylesProps) => (props.isSummaryView ? 'hidden' : undefined),
    flexShrink: 1,
  },
  filename: {
    paddingLeft: theme.spacing(2),
    color: '#86b8ff',
    display: 'inline-block',
  },
  headerText: {
    color: "#313131",
    fontSize: "1rem",
    fontWeight: "bold"
  },
  assigneeText: {
    color: '#b59f2f',
    fontSize: '1rem',
    fontWeight: 600,
  },
  statusChip: {
    width: '170px',
    justifyContent: 'center',
    fontSize: '1rem'
  },
  smallOptionsDropdown: {
    '& .MuiSvgIcon-root': {
      fontSize: '1.25rem'
    },
    '& .MuiTypography-root': {
      fontSize: '0.8rem',
    },
    paddingTop: '4px',
    paddingBottom: '4px',
  },
  unreadCountBadge: {
    '& .MuiBadge-badge': {
      top: '11px',
      right: '-2px',
      border: '2px solid',
      fontSize: '0.7rem',
    },
  },
  copyText: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    position: 'relative',
    padding: '0 4px',
    '&:hover': {
      background: theme.palette.grey[200],
      border: `1px solid ${theme.palette.grey[200]}`,
      borderLeftWidth: 0,
      borderRightWidth: 0,
      borderRadius: 16,
      '&::after': {
        display: 'inherit',
      },
    },
    '&::after': {
      display: 'none',
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      width: '100%',
      height: '100%',
    },
  },
  escalatedIcon: {
    color: 'rgb(217, 68, 82)',
  },
  escalatedTooltip: {
    border: '2px solid rgb(217, 68, 82)',
    borderRadius: '14px',
  },
  escalatedWrapper: {
    display: 'flex',
    marginLeft: '-8px',
  },
}));

interface Props {
  todos: TodoItem[];
  ccTodos?: TodoItem[];
  loanProcess: LoanProcess;
  showFilter?: boolean;
  showSectionActions?: boolean;
  showFileName?: boolean;
  showDownloadAll?: boolean;
  showDownloadTodo?: boolean;
  showActions?: boolean;
  showPriorityColumn?: boolean;
  showAssigneeColumn?: boolean;
  loanId: number;
  afterActionExecuted?: () => void;
}

export const TodoTaskCard = observer(
  ({
    todos,
    ccTodos = [],
    loanProcess,
    showFilter = false,
    showSectionActions = false,
    showFileName = false,
    showDownloadAll = false,
    showDownloadTodo = false,
    showActions = false,
    showPriorityColumn = false,
    showAssigneeColumn = false,
    loanId,
    afterActionExecuted
  }: Props) => {
    const printableAreaRef = useRef<PrintableAreaRef>(null);
    const { documentStore } = useDocumentStore();
    const { loanTodosStore } = documentStore;
    const { showOnlyPending, prioritiesFilter } = loanTodosStore;
    const [statusFilter, setStatusFilter] = useState(SHOW_ALL_TO_DOS);

    const fetchTodos = () => {
      if (loanProcess === LoanProcess.TODOS) {
        loanTodosStore.fetchTodosForMultipleLoanProcesses(loanId, loanProcess, [LoanProcess.UNDERWRITING, LoanProcess.CLOSING]);
      } else {
        loanTodosStore.fetchTodos(loanId, loanProcess);
      }
    }
    useEffect(() => {
      fetchTodos();
    }, [loanId, showOnlyPending, prioritiesFilter])

    const filteredTodos = statusFilter === SHOW_ALL_TO_DOS
      ? todos
      : filterTodosByStatus(todos, mapStatusFilter[statusFilter]);

    const download = () => {
      printableAreaRef.current.downloadAsPdf(
        `Loan ${todos[0]?.document?.loanId} To-dos.pdf`,
        {
          singlePage: true,
        }
      );
    };

    const pendingTodosWithSubtasks = filteredTodos;

    const filteredCCTodos = statusFilter === SHOW_ALL_TO_DOS
      ? ccTodos
      : filterTodosByStatus(ccTodos, mapStatusFilter[statusFilter], { includeParentTasks: false });

    const onStatusFilterChange = (val) => {
      setStatusFilter(val);
      loanTodosStore.setShowOnlyPending(mapApiShowOnlyPending[val] ?? false);
      loanTodosStore.setPrioritiesFilter(mapPriorityFilter[val] ?? []);
    }

    return (
      <CustomCard
        paperNoShadow
        paperStyle={{ padding: '0px 16px 24px 16px' }}
        title={'To-Dos'}
        cardHeaderProps={{
          action: (
            <Box pt={1} display="flex">
              {showDownloadAll && (
                <Box pr={2}>
                  <Tooltip title="Take screenshot" placement="bottom">
                    <div>
                      <Button
                        testId="downloadAll"
                        onClick={download}
                        variant="outlined"
                        style={{
                          letterSpacing: 'normal',
                          textTransform: 'none',
                          fontWeight: 'normal',
                          fontSize: '16px',
                        }}
                      >
                        <Box height={'31px'} display="flex" alignItems="center">
                          <PhotoCamera />
                          &nbsp;&nbsp;Take Screenshot
                        </Box>
                      </Button>
                    </div>
                  </Tooltip>
                </Box>
              )}
              {showFilter && (
                <StatusFilterField
                  value={statusFilter}
                  onChange={onStatusFilterChange}
                  version={showPriorityColumn ? 'v3' : 'v1'}
                />
              )}
            </Box>
          ),
        }}
        cardContentProps={{
          style: {
            overflow: 'auto',
          },
        }}
      >
        <PrintableArea ref={printableAreaRef}>
          <Box minWidth={1000}>
            <TaskTodoCardCompponent
              todos={pendingTodosWithSubtasks}
              ccTodos={filteredCCTodos}
              loanProcess={loanProcess}
              showSectionActions={showSectionActions}
              showFileName={showFileName}
              showDownloadTodo={showDownloadTodo}
              showStatusColumn={true}
              showPriorityColumn={showPriorityColumn}
              showAssigneeColumn={showAssigneeColumn}
              showDateColumn
              afterActionExecuted={afterActionExecuted}
              showActions={showActions}
            />
          </Box>
        </PrintableArea>
      </CustomCard>
    );
  }
);

export const TaskTodoCardCompponent = observer(
  ({
    todos,
    ccTodos = [],
    loanProcess,
    showSectionActions = false,
    showFileName = false,
    showDownloadTodo = false,
    showDateColumn = false,
    showStatusColumn = false,
    showPriorityColumn = false,
    showAssigneeColumn = false,
    showActions = false,
    expandAll = 0,
    collapseAll = 0,
    isLoading = false,
    isSummaryView = false,
    noRecordsMessageComponent = null,
    useAccordion = true,
    filteredBy = null,
    afterActionExecuted = undefined,
  }) => {
    const { documentStore } = useDocumentStore();
    const { loanTodosStore } = documentStore;
    const { loanStore } = useLoanStore();
    const { loanDetails } = loanStore;
    const loanId = loanDetails?.loanId;
    const isTaskFrameworkEnabled = true;
    const classes = useStyles({});

    noRecordsMessageComponent = noRecordsMessageComponent || <NoRecordsComponent filteredBy={filteredBy} />

    const formattedTodos = useMemo(() => getTodosWithSubtodos(todos), [todos]);

    useEffect(() => {
      loanTodosStore.fetchClosingConditions(loanId);
    }, [loanId]);
    return !isLoading && formattedTodos.length == 0 ? (
      <Box mx={2} mb={1}>
        {noRecordsMessageComponent}
      </Box>
    ) : (
      <TaskTodoList
        todos={formattedTodos}
        ccTodos={ccTodos}
        classes={classes}
        showDateColumn={showDateColumn}
        showStatusColumn={showStatusColumn}
        showPriorityColumn={showPriorityColumn}
        showAssigneeColumn={showAssigneeColumn}
        loanProcess={loanProcess}
        showFileName={showFileName}
        showDownloadTodo={showDownloadTodo}
        showSectionActions={showSectionActions}
        expandAll={expandAll}
        collapseAll={collapseAll}
        showActions={showActions}
        isSummaryView={isSummaryView}
        afterActionExecuted={afterActionExecuted}
        isTaskFrameworkEnabled={isTaskFrameworkEnabled}
        useAccordion={useAccordion}
      />
    );
  }
);

const NoRecordsComponent = ({ filteredBy }: { filteredBy: string }) => {

  let title = "";
  let body = <></>;
  switch (filteredBy) {
    case StatusFilterType.IN_REVIEW_OR_PENDING:
      title = "Nothing pending or in review";
      body = <>You're all caught up! Select <strong>Show All To-dos</strong> from the dropdown to view completed or other To-dos.</>
      break;
    case StatusFilterType.PENDING:
      title = "No pending to-dos";
      body = <>You're all caught up! Select <strong>Show All To-dos</strong> from the dropdown to view completed or other To-dos.</>
      break;
    case StatusFilterType.ALL_ACTIVE:
      title = "No active to-dos";
      body = <>There are no to-dos with Pending, Being Reviewed, or Accepted status. Select <strong>Show All To-dos</strong> from the dropdown to view other To-dos.</>
      break;
    case StatusFilterType.INTERNAL_ONLY:
      title = "No internal to-dos";
      body = <>There are no internal to-dos to display. Select <strong>Show All To-dos</strong> from the dropdown to view all To-dos.</>
      break;
    case TodoStatus.ALL:
      title = "No to-dos available";
      body = <>There are currently no to-dos to display.</>
      break;
    default:
      title = "No Records";
      break;
  }

  return (
    <Alert severity="info">
      <AlertTitle><strong>{title}</strong></AlertTitle>
      {body}
    </Alert>
  );
}

const countTodos = (todos: TodoItem[]): number => {
  return todos.length;
};

interface TaskTodoListProps {
  todos: TodoItem[];
  ccTodos?: TodoItem[];
  loanProcess: LoanProcess;
  showFileName?: boolean;
  showDownloadTodo?: boolean;
  showSectionActions?: boolean;
  expandAll?: number;
  collapseAll?: number;
  classes?: any;
  showDateColumn?: boolean;
  showStatusColumn?: boolean;
  showPriorityColumn?: boolean;
  showAssigneeColumn?: boolean;
  showActions?: boolean;
  isSummaryView: boolean;
  isTaskFrameworkEnabled?: boolean;
  useAccordion?: boolean;
  afterActionExecuted?: () => void;
}

const TaskTodoList: React.FC<TaskTodoListProps> = ({
  todos,
  ccTodos = [],
  loanProcess,
  showFileName,
  showDownloadTodo,
  showDateColumn = false,
  showStatusColumn = false,
  showPriorityColumn = false,
  showAssigneeColumn = false,
  showSectionActions,
  showActions,
  classes,
  expandAll,
  collapseAll,
  isSummaryView,
  isTaskFrameworkEnabled,
  useAccordion = true,
  afterActionExecuted,
}) => {
  const categories = uniq(todos.map(todo => todo.category)) as string[];
  const { globalStore } = useBaseStore();

  const ccTodosSorted = ccTodos.sort((a, b) => a.title.localeCompare(b.title));
  const shouldShowClosingConditions = ccTodosSorted?.length > 0 && loanProcess === LoanProcess.CLOSING && !(globalStore.isCrmPortal || globalStore.isLenderPortal);

  return (
    <Box>
      {(showDateColumn || showStatusColumn || (showAssigneeColumn) || (showPriorityColumn)) && <Box style={{ margin: 'auto 26px auto auto' }} display='flex' justifyContent='right'>
        <Box display="flex" justifyContent="right" style={{ minWidth: getTodoColumnsMinWidth(globalStore.isInternalPortal, isSummaryView) }}>
          {showAssigneeColumn && (
            <Box style={{ width: getColumnWidths(isSummaryView).assignee, textAlign: 'center' }}>
              <Typography variant="caption" className={classes.headerText}>Assignee</Typography>
            </Box>
          )}
          {showPriorityColumn && (
            <Box style={{ width: getColumnWidths(isSummaryView).priority, textAlign: 'center' }}>
              <Typography variant="caption" className={classes.headerText}>Priority</Typography>
            </Box>
          )}
          {showStatusColumn && (
            <Box style={{ width: getColumnWidths(isSummaryView).status, textAlign: 'center' }}>
              <Typography variant="caption" className={classes.headerText}>Status</Typography>
            </Box>
          )}
          {showDateColumn && (
            <Box style={{ width: TODO_COLUMN_WIDTHS.date, textAlign: 'center' }}>
              <Typography variant="caption" className={classes.headerText}>Last Update Date</Typography>
            </Box>
          )}
          <Box style={{ width: getColumnWidths(isSummaryView).actions }} display='flex' justifyContent='right'>
            {showStatusColumn && showDateColumn && (
              <Typography variant="caption" className={classes.headerText} style={{ paddingRight: '10px' }}>Actions</Typography>
            )}
          </Box>
        </Box>
      </Box>}
      {shouldShowClosingConditions && (
        useAccordion ? (<TaskAccordion
          key='closingConditions'
          showDateColumn={showDateColumn}
          showStatusColumn={showStatusColumn}
          showAssigneeColumn={showAssigneeColumn}
          category='Closing Conditions'
          todos={ccTodosSorted}
          loanProcess={loanProcess}
          showFileName={showFileName}
          showDownloadTodo={showDownloadTodo}
          showSectionActions={showSectionActions}
          expandAll={expandAll}
          collapseAll={collapseAll}
          showActions={showActions}
          isSummaryView={isSummaryView}
          afterActionExecuted={afterActionExecuted}
          isTaskFrameworkEnabled={isTaskFrameworkEnabled}
        />) :
          (<TaskList
            key='closingConditions'
            showDateColumn={showDateColumn}
            showStatusColumn={showStatusColumn}
            showAssigneeColumn={showAssigneeColumn}
            category='Closing Conditions'
            todos={ccTodosSorted}
            loanProcess={loanProcess}
            showFileName={showFileName}
            showDownloadTodo={showDownloadTodo}
            showSectionActions={showSectionActions}
            expandAll={expandAll}
            afterActionExecuted={afterActionExecuted}
            collapseAll={collapseAll}
            showActions={showActions}
            isSummaryView={isSummaryView}
            isTaskFrameworkEnabled={isTaskFrameworkEnabled}
          />)
      )}
      {categories.map(category => {
        const categoryTodos = todos.filter(todo => todo.category === category);
        return (
          useAccordion ? (<TaskAccordion
            key={category}
            showDateColumn={showDateColumn}
            showStatusColumn={showStatusColumn}
            showPriorityColumn={showPriorityColumn}
            showAssigneeColumn={showAssigneeColumn}
            category={category}
            todos={categoryTodos}
            loanProcess={loanProcess}
            showFileName={showFileName}
            afterActionExecuted={afterActionExecuted}
            showDownloadTodo={showDownloadTodo}
            showSectionActions={showSectionActions}
            expandAll={expandAll}
            collapseAll={collapseAll}
            showActions={showActions}
            isSummaryView={isSummaryView}
            isTaskFrameworkEnabled={isTaskFrameworkEnabled}
          />) : (
            <TaskList
              key={category}
              showDateColumn={showDateColumn}
              showStatusColumn={showStatusColumn}
              showPriorityColumn={showPriorityColumn}
              showAssigneeColumn={showAssigneeColumn}
              category={category}
              todos={categoryTodos}
              loanProcess={loanProcess}
              showFileName={showFileName}
              showDownloadTodo={showDownloadTodo}
              showSectionActions={showSectionActions}
              expandAll={expandAll}
              collapseAll={collapseAll}
              afterActionExecuted={afterActionExecuted}
              showActions={showActions}
              isSummaryView={isSummaryView}
              isTaskFrameworkEnabled={isTaskFrameworkEnabled}
            />
          )

        );
      })}
    </Box>
  );
};

interface TaskAccordionProps {
  category: string;
  todos: any[];
  loanProcess: any;
  showFileName: boolean;
  showDownloadTodo: boolean;
  showSectionActions: boolean;
  isOpen?: boolean;
  expandAll?: number;
  collapseAll?: number;
  showDateColumn?: boolean;
  showStatusColumn?: boolean;
  showPriorityColumn?: boolean;
  showAssigneeColumn?: boolean;
  showActions?: boolean;
  isSummaryView: boolean;
  isTaskFrameworkEnabled?: boolean;
  afterActionExecuted?: () => void;
}

export const TaskAccordion: React.FC<TaskAccordionProps> = ({
  category,
  todos,
  loanProcess,
  showFileName,
  showDownloadTodo,
  showSectionActions,
  isOpen = true,
  showDateColumn,
  showStatusColumn,
  showPriorityColumn,
  showAssigneeColumn,
  showActions,
  expandAll,
  collapseAll,
  isSummaryView,
  isTaskFrameworkEnabled,
  afterActionExecuted,
}) => {
  const classes = useStyles({});
  const [open, setOpen] = useState(collapseAll > expandAll ? false : expandAll > collapseAll ? true : isOpen);
  const [showCopySectionName, setShowCopySectionName] = useState(false);

  useEffect(() => {
    if (collapseAll > expandAll) setOpen(false);
    else if (expandAll > collapseAll) setOpen(true);
  }, [collapseAll, expandAll]);

  return (
    <Accordion elevation={0} expanded={open} className={classes.accordion} TransitionProps={{ unmountOnExit: true }}>
      <AccordionSummary
        expandIcon={<ExpandMore />}
        className={classes.category}
        onMouseEnter={() => {
          if (!showCopySectionName) {
            setShowCopySectionName(true)
          }
        }}
        onMouseLeave={() => {
          if (showCopySectionName) {
            setShowCopySectionName(false)
          }
        }}
        onClick={() => setOpen(prev => !prev)}
      >
        <Typography className={classes.categoryTypography}>{category}</Typography>
        {showCopySectionName && (
          <CopyText propStyles={classes} tooltipText='Copy section name' valueToCopy={category}>
            <FileCopyOutlined />
          </CopyText>
        )}
        <Box style={{ margin: 'auto 0 auto auto' }}>
          {showSectionActions && (
            <SectionActions
              loanProcess={loanProcess}
              sectionName={category}
              sectionId={todos[0]?.document.sectionId}
              categoryDB={todos[0]?.categoryDB}
              type2={todos[0]?.type2}
              typeId2={todos[0]?.typeId2}
              loanId={todos[0]?.document.loanId}
              drawId={todos[0]?.document.drawId}
              isTaskFrameworkEnabled={isTaskFrameworkEnabled}
              payoffId={todos[0]?.document.payoffId}
            />
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails style={{ flexDirection: 'column', padding: '0' }}>
        {todos.map(doc => (
          <TaskTodoItem
            key={doc.todoId}
            todo={doc}
            loanProcess={loanProcess}
            showDateColumn={showDateColumn}
            showStatusColumn={showStatusColumn}
            showPriorityColumn={showPriorityColumn}
            showAssigneeColumn={showAssigneeColumn}
            depth={0}
            showFileName={showFileName}
            showDownloadTodo={showDownloadTodo}
            expandAll={expandAll}
            collapseAll={collapseAll}
            showActions={showActions}
            isSummaryView={isSummaryView}
            afterActionExecuted={afterActionExecuted}
            isTaskFrameworkEnabled={isTaskFrameworkEnabled}
          />
        ))}
      </AccordionDetails>
    </Accordion>
  );
};

interface TaskListProps {
  category: string;
  todos: any[];
  loanProcess: any;
  showFileName: boolean;
  showDownloadTodo: boolean;
  showSectionActions: boolean;
  isOpen?: boolean;
  expandAll?: number;
  collapseAll?: number;
  showDateColumn?: boolean;
  showStatusColumn?: boolean;
  showPriorityColumn?: boolean;
  showAssigneeColumn?: boolean;
  showActions?: boolean;
  isSummaryView: boolean;
  isTaskFrameworkEnabled: boolean;
  afterActionExecuted?: () => void;
}

export const TaskList: React.FC<TaskListProps> = ({
  category,
  todos,
  loanProcess,
  showFileName,
  showDownloadTodo,
  showSectionActions,
  showDateColumn,
  showStatusColumn,
  showPriorityColumn,
  showAssigneeColumn,
  showActions,
  expandAll,
  collapseAll,
  isSummaryView,
  isTaskFrameworkEnabled,
  afterActionExecuted,
}) => {
  return (
    <Box style={{ flexDirection: 'column', padding: '0' }}>
      {todos.map(doc => (
        <TaskTodoItem
          key={doc.todoId}
          todo={doc}
          loanProcess={loanProcess}
          showDateColumn={showDateColumn}
          showStatusColumn={showStatusColumn}
          showPriorityColumn={showPriorityColumn}
          showAssigneeColumn={showAssigneeColumn}
          depth={0}
          showFileName={showFileName}
          showDownloadTodo={showDownloadTodo}
          expandAll={expandAll}
          collapseAll={collapseAll}
          showActions={showActions}
          isSummaryView={isSummaryView}
          afterActionExecuted={afterActionExecuted}
          isTaskFrameworkEnabled={isTaskFrameworkEnabled}
        />
      ))}
    </Box>
  );
};

interface TaskTodoItemProps {
  todo: TodoItem;
  loanProcess: LoanProcess;
  depth: number;
  showFileName?: boolean;
  showDownloadTodo?: boolean;
  expandAll?: number;
  collapseAll?: number;
  showDateColumn?: boolean;
  showStatusColumn?: boolean;
  showPriorityColumn?: boolean;
  showAssigneeColumn?: boolean;
  showActions?: boolean;
  isSummaryView: boolean;
  isTaskFrameworkEnabled?: boolean;
  afterActionExecuted?: () => void;
  showChatIcon?: boolean;
}

const TaskTodoItem: React.FC<TaskTodoItemProps> = observer(({
  todo,
  loanProcess,
  depth,
  showFileName,
  showDownloadTodo,
  showDateColumn,
  showStatusColumn,
  showPriorityColumn,
  showAssigneeColumn,
  showActions,
  expandAll,
  collapseAll,
  isSummaryView,
  isTaskFrameworkEnabled,
  afterActionExecuted,
  showChatIcon = true,
}) => {
  const [open, setOpen] = useState(collapseAll > expandAll ? false : true);
  const [expand, setExpand] = useState(false);
  const [openLineItemChat, setOpenLineItemChat] = useState(false);
  const { globalStore } = useBaseStore();
  const { documentStore } = useDocumentStore();
  const { userStore } = useUserStore();
  const allowLoanPricing = userStore.allowLoanPricing;
  const classes = useStyles({ depth, isSummaryView });
  const theme = useTheme();
  const { generalTasksStore } = useGeneralTasksStore();

  const info = todo.document?.documentNameSecondLine || null;
  const title = documentStore.getDisplayDocumentName(todo.title, todo?.document);
  const filename = todo.document?.originalFileName;
  const tooltip = todo.document?.taskInformation;
  const hasSubTodos = todo.subTodos?.length > 0;
  const isParentTask = todo.document?.isParentTask;
  const hideUploadTab = shouldHideTaskUploadTab({ isParentTask, taskName: todo.title });
  const loanId = todo.document?.loanId;

  const shouldShowLineItemChatOptions = allowLoanPricing && !userStore.isJrBroker && !userStore.isJrOriginator;

  enum TaskStatusColors {
    'PENDING' = 'rgb(217, 68, 82)',
    'BEING_REVIEWED' = 'rgb(221, 167, 0)',
    'MORE_INFO_NEEDED' = 'rgb(217, 68, 82)',
    'ACCEPTED' = 'rgb(72, 173, 37)',
    'NOT_APPLICABLE' = 'rgba(0, 0, 0, 0.54)',
  }

  const statusCircle = (status: string) => {
    const color = TaskStatusColors[status];
    return <div style={{ height: 16, width: 16, borderRadius: '50%', backgroundColor: color, marginRight: 5 }}>
    </div>

  }

  useEffect(() => {
    if (collapseAll > expandAll) setOpen(false);
    else if (expandAll > collapseAll) setOpen(true);
  }, [collapseAll, expandAll]);

  const showTodoLink = !isParentTask && (todo?.document?.isForm || todo.document.taskId == null || !shouldShowLineItemChatOptions || todo.document.manualTask);
  const isEscalated = todo?.taskTags?.some((tag) => tag.tagType === ESCALATED_TASK_TYPE)
    || generalTasksStore.isTaskEscalated(todo?.document?.taskId);

  const handleOpenChat = (openChat) => {
    if (showTodoLink) {
      documentStore.onOpenForm(todo.document);
    } else {
      setOpenLineItemChat(true);
    }

    if (openChat && todo.document?.taskId) {
      generalTasksStore.setOpenChatTabForTaskId(todo.document?.taskId, todo.document?.isParentTask);
    } else {
      generalTasksStore.setOpenChatTabForTaskId(null);
    }
  }

  return (
    <>
      <Box>
        <Box
          className={classname(classes.item, {
            [classes.itemHasIcon]: hasSubTodos,
          })}
        >
          {hasSubTodos ? (
            <IconButton size="small" className={classes.icon} testId={''} onClick={() => setOpen(!open)}>
              {open ? <Remove /> : <Add />}
            </IconButton>
          ) : null}
          {todo.document?.closingCondition && (
            <Box flexShrink={0} display="flex" alignItems="center">
              <Tooltip
                title={<Box dangerouslySetInnerHTML={{ __html: sanitizeHtml(closingConditionTooltipMessageHTML) }} />}
                placement='top'
                closingConditionLevel={todo.document.closingCondition.level}
                iconColor='secondary'
                closingCondition
                arrow>
                {todo.document.closingCondition.level === closingConditionsLevel.LEVEL_2 ? <ClosingConditionIcon /> : <ClosingConditionWarning />}
              </Tooltip>
            </Box>
          )}
          {todo.document?.postClosingCondition && (
            <Box flexShrink={0} display="flex" alignItems="center">
              <Tooltip
                title={<Box dangerouslySetInnerHTML={{ __html: sanitizeHtml(postClosingConditionTooltipMessageHTML) }} />}
                placement='top'
                arrow>
                <PostClosingConditionIcon />
              </Tooltip>
            </Box>
          )}
          <Box
            style={{
              minWidth: isSummaryView ? '120px' : undefined,
              maxWidth: isSummaryView ? undefined : '40%',
              overflow: 'hidden',
            }}
            display="flex"
            flexWrap="nowrap"
            alignItems="center"
          >
            <TooltipOnOverflow
              title={title}
              placement="bottom"
              spanClass={classes.todoTitle}
            >
              {(showTodoLink) ?
                <Typography component="div" className={classes.overflowElipsis}>
                  <TodoTitleLink document={todo.document} loanProcess={loanProcess} propertyId={todo.typeId2}>
                    {title}
                    {info ? ` - ${info}` : null}
                  </TodoTitleLink>
                </Typography> :
                <Typography component="div" className={`${classes.overflowElipsis} ${hasSubTodos ? classes.bold : classes.underlined}`} onClick={() => handleOpenChat(isParentTask)} >
                  {title}
                  {info ? ` - ${info}` : null}
                  {hasSubTodos ? ` (${todo.subTodos.length})` : ''}
                </Typography>
              }
            </TooltipOnOverflow>
            {!isSummaryView && globalStore.isInternalPortal && hasSubTodos && (
              <TaskScopeIcon isInternal={!!todo.document?.internalTask} />
            )}
            {!!tooltip && (
              <Tooltip
                interactive
                style={{ color: '#777' }}
                title={<Box dangerouslySetInnerHTML={{ __html: sanitizeHtml(tooltip) }} />}
              />
            )}
            {showFileName && !!filename && (
              <TooltipOnOverflow
                title={filename}
                placement="bottom"
                spanClass={classes.filename}
              >
                <Typography className={classes.overflowElipsis}>
                  {filename}
                </Typography>
              </TooltipOnOverflow>
            )}
            {showDownloadTodo && <DownloadTodoButton document={todo.document} />}
          </Box>
          {shouldShowLineItemChatOptions && showChatIcon &&
            todo.document.taskId != null &&
            <Box flexShrink={0}>
              <IconButton testId="downloadAll"
                onClick={() => handleOpenChat(true)}
                style={{
                  letterSpacing: 'normal',
                  textTransform: 'none',
                  fontWeight: 'normal',
                  fontSize: '16px',
                  padding: '6px',
                  marginLeft: '-8px',
                }}>
                <Badge className={classes.unreadCountBadge} color="error" badgeContent={todo?.document?.unreadCount}>
                  {!!(todo?.document?.unreadCount) ? (
                    <ChatBubble fill={theme.palette.error.main} />
                  ) : (
                    <ChatBubble />
                  )}
                </Badge>
              </IconButton>
              { }
            </Box>
          }
          {isEscalated && (
            <Box className={classes.escalatedWrapper} paddingLeft={showChatIcon ? '0px' : '8px'}>
              <Tooltip title="Escalated" className={classes.escalatedTooltip}>
                <ArrowUpwardRounded className={classes.escalatedIcon} />
              </Tooltip>
            </Box>
          )}
          {!isParentTask && (
            <>
              {globalStore.isInternalPortal ? (
                <Box style={{ margin: 'auto 26px auto auto', alignItems: 'center', flexShrink: 0 }} display="flex" justifyContent="right">
                  <Box display='flex' style={{ minWidth: getTodoColumnsMinWidth(true, isSummaryView) }} justifyContent='right'>
                    {showAssigneeColumn && (
                      <Box style={{ width: getColumnWidths(isSummaryView).assignee, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="caption" style={{ color: '#555', fontSize: '1rem' }}>{todo.document?.taskAssignee}</Typography>
                      </Box>
                    )}
                    {showPriorityColumn && (
                      <Box style={{ width: getColumnWidths(isSummaryView).priority, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {isNotBlank(todo?.taskPriority) && (
                          <StatusChip
                            statusType={StatusType.PRIORITY}
                            label={todo?.taskPriority}
                            size="small"
                          />
                        )}
                      </Box>
                    )}
                    {showStatusColumn && <Box style={{ width: getColumnWidths(isSummaryView).status, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <TodoStatusChip
                        status={todo.status}
                        onStatusChange={status => {
                          documentStore.loanTodosStore.updateTodoStatus(
                            loanId,
                            loanProcess,
                            todo.document,
                            todoStatusMapping[status],
                            afterActionExecuted
                          );
                        }}
                        size='small'
                        statusClassname={classes.statusChip}
                        dropdownClassname={classes.smallOptionsDropdown}
                      />
                    </Box>}
                    {showDateColumn && <Box style={{ width: TODO_COLUMN_WIDTHS.date, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="caption" style={{ color: '#555', fontSize: '1rem' }}>{todo.lastUpdateDate}</Typography>
                    </Box>}
                    {todo.document ? (
                      <Box style={{ width: getColumnWidths(isSummaryView).actions }} display='flex' justifyContent='right'><TodoActions typeId2={todo.typeId2} document={todo.document} loanProcess={loanProcess} /></Box>
                    ) : (
                      <Box style={{ width: getColumnWidths(isSummaryView).actions }} display='flex' justifyContent='right'>
                        <IconButton
                          testId={`todo-${todo.todoId}-more`}
                          onClick={() => { }}
                        >
                          <MoreHorizIcon />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </Box>
              ) : (
                <Box style={{ margin: 'auto 26px auto auto', alignItems: 'center', flexShrink: 0 }} display="flex" justifyContent="right">
                  <Box display='flex' style={{ minWidth: getTodoColumnsMinWidth(false, isSummaryView) }} justifyContent='right'>
                    {showAssigneeColumn && (
                      <Box style={{ width: getColumnWidths(isSummaryView).assignee, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography
                          variant="caption"
                          className={todo?.isTpoRole ? undefined : classes.assigneeText}
                          style={todo?.isTpoRole ? { color: theme.palette.text.primary, fontSize: '1rem' } : undefined}
                        >
                          {todo?.document?.taskAssignee ?? todo.taskAssignee}
                        </Typography>
                      </Box>
                    )}
                    {showPriorityColumn && (
                      <Box style={{ width: getColumnWidths(isSummaryView).priority, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <StatusChip
                          statusType={StatusType.PRIORITY}
                          label={todo?.taskPriority}
                          size="small"
                        />
                      </Box>
                    )}
                    {showStatusColumn && (
                      <Box style={{ width: getColumnWidths(isSummaryView).status, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <TodoStatusChip
                          showDropdown={false}
                          status={todo.status}
                          onStatusChange={status => { }}
                          size='small'
                          statusClassname={classes.statusChip}
                        />
                      </Box>
                    )}
                    {showDateColumn && <Box style={{ width: TODO_COLUMN_WIDTHS.date, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="caption" style={{ color: '#555', fontSize: '1rem' }}>{todo.lastUpdateDate}</Typography>
                    </Box>}
                    <Box style={{ width: getColumnWidths(isSummaryView).actions }} display='flex' justifyContent='right'>
                      {showActions && (
                        todo.document ? (
                          <TodoActions typeId2={todo.typeId2} document={todo.document} loanProcess={loanProcess} />
                        ) : (
                          <IconButton
                            testId={`todo-${todo.todoId}-more`}
                            onClick={() => { }}
                          >
                            <MoreHorizIcon />
                          </IconButton>
                        )
                      )}
                    </Box>
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>
        {open && todo.subTodos && (
          <Box border={0} className={classes.subItem}>
            {todo.subTodos.map(sub => (
              <TaskTodoItem
                key={sub.todoId}
                todo={sub}
                loanProcess={loanProcess}
                showDateColumn={showDateColumn}
                depth={depth + 1}
                showFileName={showFileName}
                showDownloadTodo={showDownloadTodo}
                showActions={showActions}
                isSummaryView={isSummaryView}
                isTaskFrameworkEnabled={isTaskFrameworkEnabled}
                showStatusColumn={showStatusColumn}
                showPriorityColumn={showPriorityColumn}
                showAssigneeColumn={showAssigneeColumn}
                showChatIcon={false}
                expandAll={expandAll}
                collapseAll={collapseAll}
              />
            ))}
          </Box>
        )}
      </Box>
      {openLineItemChat && (
        <CustomTaskDialog
          title={todo.title}
          taskId={todo.document?.taskId}
          open={openLineItemChat}
          dialogContent={hideUploadTab ? null : <LineItemChat todo={todo} expand={true} />}
          handleClose={() => {
            documentStore.closeDocumentPreviewV2();
            if (loanProcess === LoanProcess.TODOS) {
              documentStore.loanTodosStore.fetchTodosForMultipleLoanProcesses(todo.document.loanId, loanProcess, [LoanProcess.UNDERWRITING, LoanProcess.CLOSING]);
            } else {
              documentStore.loanTodosStore.fetchTodos(todo.document.loanId, loanProcess);
            }
            setOpenLineItemChat(false);
          }}
        />
      )}
    </>
  );
});
